/**
 * Bookd - Talent Marketplace Platform
 * Copyright © 2025 Bookd. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { BookProvider } from './context/BookContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ProfileCompletionModal from './components/ProfileCompletionModal';
import { toast } from 'sonner';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Browse from './pages/Browse';
import TalentProfile from './pages/TalentProfile';
import Dashboard from './pages/Dashboard';
import Bookings from './pages/Bookings';
import Opportunities from './pages/Opportunities';
import Settings from './pages/Settings';
import MyServices from './pages/MyServices';
import Messages from './pages/Messages';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Calendar from './pages/Calendar';
import AdminPanel from './pages/AdminPanel';
import Subscriptions from './pages/Subscriptions';
import CancellationPolicy from './pages/CancellationPolicy';
import ContactUs from './pages/ContactUs';
import Venues from './pages/Venues';
import VenueDetail from './pages/VenueDetail';
import ListVenue from './pages/ListVenue';
import MyBook from './pages/MyBook';
import Favorites from './pages/Favorites';
import Support from './pages/Support';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Main Router Component
function AppRouter() {
  const location = useLocation();
  
  // Check for OAuth callback - must be synchronous, before Routes render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Determine if we should show navbar
  const hideNavbarPaths = ['/login', '/register', '/auth/callback'];
  const showNavbar = !hideNavbarPaths.includes(location.pathname);

  return (
    <>
      <ScrollToTop />
      {showNavbar && <Navbar />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/talent/:id" element={<TalentProfile />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/subscriptions" element={<Subscriptions />} />
        <Route path="/cancellation-policy" element={<CancellationPolicy />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/venues" element={<Venues />} />
        <Route path="/venues/:id" element={<VenueDetail />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute><Dashboard /></ProtectedRoute>
        } />
        <Route path="/bookings" element={
          <ProtectedRoute><Bookings /></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute><Settings /></ProtectedRoute>
        } />
        <Route path="/favorites" element={
          <ProtectedRoute><Favorites /></ProtectedRoute>
        } />
        <Route path="/my-services" element={
          <ProtectedRoute><MyServices /></ProtectedRoute>
        } />
        <Route path="/messages" element={
          <ProtectedRoute><Messages /></ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute><Calendar /></ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute><AdminPanel /></ProtectedRoute>
        } />
        <Route path="/my-book" element={
          <ProtectedRoute><MyBook /></ProtectedRoute>
        } />
        <Route path="/support" element={
          <ProtectedRoute><Support /></ProtectedRoute>
        } />
        <Route path="/list-venue" element={
          <ProtectedRoute><ListVenue /></ProtectedRoute>
        } />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// Profile Completion Handler - listens for 403 profile_incomplete errors
const ProfileCompletionHandler = () => {
  const [showModal, setShowModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    const handleProfileIncomplete = (event) => {
      setErrorDetails(event.detail);
      setShowModal(true);
      toast.error(event.detail.message || 'Please complete your profile to continue');
    };

    window.addEventListener('profileIncomplete', handleProfileIncomplete);
    return () => window.removeEventListener('profileIncomplete', handleProfileIncomplete);
  }, []);

  return (
    <ProfileCompletionModal
      isOpen={showModal}
      onClose={() => {
        setShowModal(false);
        setErrorDetails(null);
      }}
      onComplete={() => {
        setShowModal(false);
        setErrorDetails(null);
        toast.success('Profile updated! You can now continue.');
      }}
    />
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BookProvider>
            <BrowserRouter>
              <div className="min-h-screen bg-background flex flex-col">
                <AppRouter />
                <Footer />
                <Toaster position="top-right" richColors />
                <PWAInstallPrompt />
                <ProfileCompletionHandler />
              </div>
            </BrowserRouter>
          </BookProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
